import Phaser from "phaser";
import { PrimitiveAvatar } from "../../../avatars/PrimitiveAvatar";
import { PlayerAvatarView } from "../../../avatars/PlayerAvatarView";
import { GOBLIN_BLUEPRINT } from "../../../avatars/blueprints/goblin";
import { EnemyFactory } from "../../../data/enemyFactory";
import { BossFactory, BossId } from "../../../data/bossFactory";
import { getCharacterAvatar } from "../../../avatars/characterAvatars";
import { SkillId } from "../../../data/skills";
import { layoutEnemyRow } from "../../../avatars/layout/enemyRow";
import { COLORS, HUD_HEIGHT, LAYOUT } from "../../../constants";
import { EnemyInstance } from "../../../data/enemies";
import { CombatEvent } from "../../../state/combatEvents";
import { CombatEngine, CombatSnapshot, CombatStep } from "../../../state/combat";
import { EncounterEnemyConfig, buildEncounterEnemies } from "../../../state/encounterEnemies";
import { RunState, newRun, isAtMaxFloor, markRunDefeat } from "../../../state/runState";
import { spawnCombatText } from "../../../ui/combatText";
import { Button } from "../../../ui/Button";
import { EncounterScene } from "../../EncounterScene";
import { BaseScreen } from "../../../screens/BaseScreen";
import { registerAutomationTarget } from "../../../automation/automationTags";
import { PrimitiveIconId } from "../../../avatars/icons/primitiveIcons";
import { OverworldState } from "../../../state/overworldState";
import { applyEncounterVictory } from "../../../state/overworldLogic";
import type { ScreenContext } from "../../../screens/types";
import { RUN_CHANGED_EVENT } from "../../RunHudScene";

type EnemyView = {
  instance: EnemyInstance;
  avatar: PrimitiveAvatar;
  container: Phaser.GameObjects.Container;
};

const ENEMY_ATTACK_DELAY = 320;
const ACTION_WINDUP = 200;
const ATTACK_LINE_DURATION = 240;
const HIGHLIGHT_SCALE = 1.05;
const ENEMY_AVATAR_SCALE = 0.73;
const PLAYER_AVATAR_SCALE = 0.71;
const ATTACK_LINE_DEPTH = 20;
const OUTCOME_TRANSITION_DELAY = 800;

export class EncounterScreen extends BaseScreen {
  private run!: RunState;
  private enemies: EnemyInstance[] = [];
  private enemyViews: EnemyView[] = [];
  private isResolving = false;
  private isPlayerTurn = true;
  private selectedEnemyIndex = -1;
  private enemyConfig?: EncounterEnemyConfig;
  private overworldState?: OverworldState;
  private commandMenuContainer?: Phaser.GameObjects.Container;
  private commandButtons: Button[] = [];
  private commandPulseTween?: Phaser.Tweens.Tween;
  private activeEnemyIndex = -1;
  private engine?: CombatEngine;
  private actionOptions: ReturnType<CombatEngine["getAvailableActions"]> = [];

  private playerAvatarView?: PlayerAvatarView;
  private playerRect?: Phaser.GameObjects.Rectangle;

  constructor(scene: EncounterScene) {
    super(scene, "ENCOUNTER");
  }

  enter(context?: ScreenContext) {
    this.run = context?.run ?? newRun();
    this.isResolving = false;
    this.isPlayerTurn = true;
    this.enemies = [];
    this.enemyViews = [];
    this.selectedEnemyIndex = -1;
    this.enemyConfig = context?.enemyConfig;
    this.overworldState = context?.overworldState ?? this.scene.getOverworldState?.();
    this.commandButtons = [];
    this.commandPulseTween?.stop();
    this.commandPulseTween = undefined;
    this.activeEnemyIndex = -1;

    // If this is a boss encounter from the prompt screen, keep the flag
    // Otherwise, clear it (e.g., if returning to overworld and starting a new encounter)
    // The flag will be set again when floor is complete

    this.initializeCombat();
    this.renderUI();
  }

  exit() {
    this.commandPulseTween?.stop();
    this.commandPulseTween = undefined;
    super.exit();
  }

  private initializeCombat() {
    const enemies = buildEncounterEnemies({
      floor: this.run.floor,
      config: this.enemyConfig,
    });

    this.engine = new CombatEngine({
      run: this.run,
      enemies,
    });

    this.actionOptions = this.engine.getAvailableActions();
    this.applySnapshot(this.engine.getSnapshot());
  }

  private applySnapshot(snapshot: CombatSnapshot) {
    this.run = snapshot.run;
    this.enemies = snapshot.enemies;
    this.isPlayerTurn = snapshot.turn === "player";
    this.actionOptions = this.engine?.getAvailableActions() ?? this.actionOptions;

    // Keep the persistent HUD in sync with authoritative run state.
    this.scene.game.events.emit(RUN_CHANGED_EVENT, this.run);

    this.enemyViews.forEach((view, index) => {
      const instance = this.enemies[index];
      if (instance) {
        view.instance = instance;
      }
    });

    // Update player UI to reflect defending state and other changes
    if (this.playerAvatarView) {
      this.playerAvatarView.setDefending(this.run.player.isDefending, this.run.player.armor);
    }
  }

  private renderUI() {
    const { width, height } = this.scene.scale;
    const paddingX = LAYOUT.screenPadding;
    const paddingTop = LAYOUT.screenPadding + HUD_HEIGHT;

    const headerHeight = this.renderBattleHeader(paddingX, paddingTop);

    const commandMenuHeight = this.getCommandMenuHeight();
    const commandMenuY = height - paddingX - commandMenuHeight;
    const enemyPanelStartY = paddingTop + headerHeight;

    // Get player avatar based on character ID
    const playerBlueprint = getCharacterAvatar(this.run.characterId);
    const measured = PrimitiveAvatar.measure(playerBlueprint, { scale: PLAYER_AVATAR_SCALE });
    const playerAvatarWidth = measured.width;
    const playerAvatarHeight = measured.height;
    const playerAvatarX = width / 2 - playerAvatarWidth / 2;
    const playerAvatarY = commandMenuY - playerAvatarHeight - 14;

    const contentWidth = width - paddingX * 2;
    this.renderEnemyPanels(paddingX, enemyPanelStartY, contentWidth);
    this.renderPlayerAvatar(
      playerBlueprint,
      playerAvatarX,
      playerAvatarY,
      playerAvatarWidth,
      playerAvatarHeight
    );

    this.renderCommandMenu(paddingX, width - paddingX * 2, commandMenuY);

    this.registerAutomationTags();

    this.selectedEnemyIndex = this.getFirstAliveIndex();
    this.ensureValidSelection();
  }

  private isBossEncounter(): boolean {
    // Check if run state indicates boss encounter or if any enemy is a boss
    if (this.run.isBossEncounter) {
      return true;
    }
    // Check if enemy config contains boss IDs
    if (this.enemyConfig?.enemies.some((e) => e.id.startsWith("boss_"))) {
      return true;
    }
    return false;
  }

  private renderBattleHeader(paddingX: number, paddingTop: number): number {
    const { width } = this.scene.scale;
    const bgWidth = width - paddingX * 2;
    const bgHeight = 52;

    const isBoss = this.isBossEncounter();

    // Use different colors for boss encounters
    const bgColor = isBoss ? COLORS.perkCardFill : COLORS.panelFill;
    const strokeColor = isBoss ? COLORS.perkCardStroke : COLORS.panelStroke;

    const bg = this.scene.add
      .rectangle(paddingX, paddingTop, bgWidth, bgHeight, bgColor)
      .setOrigin(0, 0);
    bg.setStrokeStyle(isBoss ? 3 : 2, strokeColor);
    this.addToContainer(bg);

    this.trackCleanup(
      registerAutomationTarget(this.scene, {
        type: "label",
        name: "encounter-header",
        getBounds: () => bg.getBounds(),
      })
    );

    return bgHeight;
  }

  private renderPlayerAvatar(blueprint: any, x: number, y: number, width: number, height: number) {
    this.playerRect = this.scene.add
      .rectangle(x + width / 2, y + height / 2, width, height, COLORS.panelFill, 0)
      .setOrigin(0.5, 0.5);
    this.addToContainer(this.playerRect);

    this.playerAvatarView = new PlayerAvatarView(this.scene, blueprint, {
      scale: PLAYER_AVATAR_SCALE,
      selectionColor: COLORS.buttonStroke,
    });
    this.playerAvatarView.setPosition(x, y);
    this.playerAvatarView.setAlive(this.run.player.hp > 0);
    this.addToContainer(this.playerAvatarView.getRoot());
  }

  private renderEnemyPanels(x: number, startY: number, width: number) {
    const gap = 24;
    const healthBarHeight = 22;
    // Use first enemy's blueprint for measurement (assumes uniform sizing for now)
    const measureBlueprint =
      this.enemies.length > 0 ? EnemyFactory.getBlueprint(this.enemies[0].id) : GOBLIN_BLUEPRINT;
    const measured = PrimitiveAvatar.measure(measureBlueprint, {
      scale: ENEMY_AVATAR_SCALE,
      showHealthBar: true,
      healthBarOptions: { height: healthBarHeight },
      nextAction: { intentType: "attack", value: 0 },
    });
    const avatarWidth = measured.width;
    const avatarHeight = measured.height;
    const containerHeight = avatarHeight;
    const areaHeight = Math.max(260, containerHeight + 20);
    const areaRect = new Phaser.Geom.Rectangle(x, startY, width, areaHeight);
    const positions = layoutEnemyRow(
      this.enemies.length,
      areaRect,
      avatarWidth,
      containerHeight,
      gap
    );

    const isBoss = this.isBossEncounter();
    // Boss avatars are larger (1.2x scale)
    const bossScale = isBoss ? ENEMY_AVATAR_SCALE * 1.2 : ENEMY_AVATAR_SCALE;

    this.enemyViews = this.enemies.map((enemy, index) => {
      const enemyIsBoss = enemy.id.startsWith("boss_") || isBoss;
      const pos = positions[index];
      const container = this.scene.add.container(
        pos.x - avatarWidth / 2,
        pos.y - containerHeight / 2
      );
      container.setSize(avatarWidth, containerHeight);
      container.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, avatarWidth, containerHeight),
        Phaser.Geom.Rectangle.Contains
      );
      if (container.input) {
        container.input.cursor = "pointer";
      }

      // Get blueprint - use BossFactory for bosses, EnemyFactory for regular enemies
      const blueprint =
        enemyIsBoss && enemy.id.startsWith("boss_")
          ? BossFactory.getBlueprint(enemy.id as BossId)
          : EnemyFactory.getBlueprint(enemy.id);

      const avatar = new PrimitiveAvatar(this.scene, blueprint, {
        scale: enemyIsBoss ? bossScale : ENEMY_AVATAR_SCALE,
        selectionColor: enemyIsBoss ? COLORS.perkCardStroke : COLORS.buttonStroke,
        showHealthBar: true,
        health: { current: enemy.hp, max: enemy.maxHp },
        healthBarOptions: {
          height: healthBarHeight,
          // Boss health bars use different colors
          fillColor: enemyIsBoss ? COLORS.perkCardStroke : undefined,
        },
        nextAction: {
          intentType: enemy.intent,
          value: enemy.intent === "defend" ? enemy.armor : enemy.atk,
          isActive: false,
        },
      });
      const avatarRoot = avatar.getRoot();
      avatarRoot.setPosition(0, 0);

      container.add(avatarRoot);

      // Add boss glow effect
      if (enemyIsBoss) {
        const glowRadius = Math.max(avatarWidth, containerHeight) * 0.6;
        const glow = this.scene.add.circle(
          avatarWidth / 2,
          containerHeight / 2,
          glowRadius,
          COLORS.perkCardStroke,
          0.2
        );
        glow.setBlendMode(Phaser.BlendModes.ADD);
        container.addAt(glow, 0); // Add glow behind avatar

        // Animate glow pulse
        this.addTween({
          targets: glow,
          alpha: { from: 0.2, to: 0.4 },
          scale: { from: 1.0, to: 1.1 },
          duration: 1000,
          ease: "Sine.easeInOut",
          yoyo: true,
          repeat: -1,
        });
      }

      const hitZone = this.scene.add
        .zone(pos.x, pos.y, avatarWidth, containerHeight)
        .setOrigin(0.5)
        .setInteractive({ cursor: "pointer" });

      hitZone.on("pointerdown", () => {
        if (!enemy.isAlive()) {
          return;
        }
        this.selectedEnemyIndex = index;
        this.updateSelectionVisuals();
      });

      this.trackCleanup(
        registerAutomationTarget(this.scene, {
          type: "button",
          name: `enemy-${index + 1}`,
          getBounds: () => hitZone.getBounds(),
          getText: () => enemy.name,
        })
      );

      this.trackCleanup(
        registerAutomationTarget(this.scene, {
          type: "label",
          name: `enemy-${index + 1}-hp`,
          getBounds: () => hitZone.getBounds(),
          getText: () => {
            const live = this.enemyViews[index]?.instance;
            return live ? `${live.hp}/${live.maxHp}` : `${enemy.hp}/${enemy.maxHp}`;
          },
        })
      );

      this.trackCleanup(
        registerAutomationTarget(this.scene, {
          type: "label",
          name: `enemy-${index + 1}-intent`,
          getBounds: () => hitZone.getBounds(),
          getText: () => {
            const live = this.enemyViews[index]?.instance;
            const intent = live?.intent ?? enemy.intent;
            const value =
              intent === "defend" ? (live?.armor ?? enemy.armor) : (live?.atk ?? enemy.atk);
            return `${intent}:${value}`;
          },
        })
      );

      this.addToContainer(container);
      this.addToContainer(hitZone);

      return { instance: enemy, avatar, container };
    });

    this.updateEnemyUI();
  }

  private getCommandMenuHeight() {
    const skillCount = this.actionOptions.length;
    return skillCount > 0 ? LAYOUT.buttonHeight : 0;
  }

  private getSkillIconId(skillId: SkillId): PrimitiveIconId {
    const iconMap: Record<SkillId, PrimitiveIconId> = {
      attack: "attack",
      defend: "defend",
      slash: "slash",
      bloodPact: "default",
    };
    return iconMap[skillId] ?? "default";
  }

  private renderCommandMenu(x: number, buttonWidth: number, startY: number) {
    const skills = this.actionOptions;
    const totalHeight = this.getCommandMenuHeight();
    const spacing = skills.length > 1 ? LAYOUT.buttonSpacing : 0;
    const totalSpacing = Math.max(0, skills.length - 1) * spacing;
    const singleButtonWidth =
      skills.length > 0 ? (buttonWidth - totalSpacing) / skills.length : buttonWidth;

    const containerX = x + buttonWidth / 2;
    const containerY = startY + totalHeight / 2;
    this.commandMenuContainer = this.scene.add.container(containerX, containerY);
    this.commandMenuContainer.setSize(buttonWidth, totalHeight);
    this.commandMenuContainer.setScale(1);
    this.addToContainer(this.commandMenuContainer);

    const startX = -buttonWidth / 2;
    const startYLocal = -totalHeight / 2;

    skills.forEach((skill, index) => {
      const buttonX = startX + index * (singleButtonWidth + spacing);
      const button = new Button(this.scene, buttonX, startYLocal, {
        width: singleButtonWidth,
        height: LAYOUT.buttonHeight,
        label: skill.label,
        icon: this.getSkillIconId(skill.skillId),
        automationName: `skill-${skill.skillId}`,
        onClick: () => this.handlePlayerSkill(skill.skillId),
      });
      this.commandButtons.push(button);
      this.commandMenuContainer?.add(button);
    });

    this.updateCommandMenuState(true);
  }

  private updatePlayerUI() {
    if (this.playerAvatarView) {
      this.playerAvatarView.setAlive(this.run.player.hp > 0);
      this.playerAvatarView.setDefending(this.run.player.isDefending, this.run.player.armor);
    }
  }

  private updateEnemyUI() {
    this.enemyViews.forEach((view, index) => {
      const alive = view.instance.isAlive();
      view.avatar.setAlive(alive);
      // Update health bar if present
      if (typeof view.instance.hp === "number" && typeof view.instance.maxHp === "number") {
        view.avatar.setHealth(view.instance.hp, view.instance.maxHp);
      }
      // Show/hide defending bubble based on isDefending state
      view.avatar.setDefending(alive && view.instance.isDefending);

      const isActive = this.activeEnemyIndex === index && alive;
      const intent = view.instance.intent;
      view.avatar.setNextAction(
        alive
          ? {
              intentType: intent,
              value: intent === "defend" ? view.instance.armor : view.instance.atk,
              isActive,
            }
          : undefined
      );
    });
    this.updateSelectionVisuals();
  }

  private async handlePlayerSkill(skillId: SkillId) {
    if (!this.engine || this.isResolving || !this.isPlayerTurn) {
      return;
    }

    const action = this.actionOptions.find((option) => option.skillId === skillId);
    if (!action) {
      return;
    }

    if (action.requiresTarget) {
      this.ensureValidSelection();
      const targetEnemy = this.enemies[this.selectedEnemyIndex];
      if (!targetEnemy || !targetEnemy.isAlive()) {
        return;
      }
    }

    this.isResolving = true;
    this.isPlayerTurn = false;
    this.updateCommandMenuState();

    const result = this.engine.takePlayerAction({
      kind: "useSkill",
      skillId,
      targetIndex: action.requiresTarget ? this.selectedEnemyIndex : undefined,
    });

    this.applySnapshot(result.snapshot);

    await this.playCombatEvents(result.events);

    if (this.routeAfterResolution(result.snapshot, true)) {
      return;
    }

    if (result.snapshot.turn === "enemy") {
      await this.wait(ENEMY_ATTACK_DELAY);
      void this.resolveEnemiesTurn();
    } else {
      this.startPlayerTurn();
    }
  }

  private async resolveEnemiesTurn() {
    if (!this.engine) {
      return;
    }

    this.isResolving = true;
    this.isPlayerTurn = false;
    this.updateCommandMenuState();

    const result = this.engine.takeEnemyTurn();
    const steps: CombatStep[] =
      result.steps && result.steps.length > 0
        ? result.steps
        : result.events.map((event) => ({ event, snapshot: result.snapshot }));

    for (const step of steps) {
      if (step.event.kind === "enemyAttack") {
        this.activeEnemyIndex = step.event.attackerIndex;
        this.updateEnemyUI();
        await this.playEnemyAttackStep(step.event, step.snapshot);
      } else if (step.event.kind === "enemyDefend") {
        this.activeEnemyIndex = step.event.attackerIndex;
        this.updateEnemyUI();
        await this.playEnemyDefendStep(step.event, step.snapshot);
      } else {
        await this.playCombatEvents([step.event]);
        this.applySnapshot(step.snapshot);
      }

      if (this.routeAfterResolution(step.snapshot, false)) {
        this.activeEnemyIndex = -1;
        return;
      }

      await this.wait(ENEMY_ATTACK_DELAY);
    }

    this.activeEnemyIndex = -1;
    this.applySnapshot(result.snapshot);
    this.updateEnemyUI();

    if (this.routeAfterResolution(result.snapshot, false)) {
      return;
    }

    this.startPlayerTurn();
  }

  private async playEnemyDefendStep(
    event: Extract<CombatEvent, { kind: "enemyDefend" }>,
    snapshot: CombatSnapshot
  ) {
    const view = this.enemyViews[event.attackerIndex];
    if (view) {
      // Visual feedback for defending - pulse/shield effect
      const bounds = view.avatar.getWorldBounds();

      // Create a shield glow effect
      const shieldColor = 0x60a5fa;
      const shieldOverlay = this.scene.add.rectangle(
        bounds.centerX,
        bounds.centerY,
        bounds.width * 1.1,
        bounds.height * 1.1,
        shieldColor,
        0.25
      );
      shieldOverlay.setStrokeStyle(3, shieldColor, 0.8);
      shieldOverlay.setDepth((view.container.depth ?? 0) + 30);

      // Animate the shield effect
      this.scene.tweens.add({
        targets: shieldOverlay,
        alpha: 0,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 400,
        ease: "Sine.easeOut",
        onComplete: () => shieldOverlay.destroy(),
      });

      // No combat text needed - the intent indicator already shows armor value
    }

    this.applySnapshot(snapshot);
  }

  private showDamageOnEnemy(targetIndex: number, amount: number) {
    const view = this.enemyViews[targetIndex];
    if (view) {
      view.avatar.playHit();
      const bounds = view.avatar.getWorldBounds();
      spawnCombatText(this.scene, {
        kind: "damage",
        value: amount,
        x: bounds.centerX,
        y: bounds.top + 12,
      });
    }
    this.updateEnemyUI();
    if (!this.enemies[targetIndex]?.isAlive()) {
      this.ensureValidSelection();
    }
  }

  private showDamageOnPlayer(amount: number) {
    this.playerAvatarView?.playHit();
    const bounds = this.playerAvatarView?.getWorldBounds();
    if (bounds) {
      spawnCombatText(this.scene, {
        kind: "damage",
        value: amount,
        x: bounds.centerX,
        y: bounds.top + 12,
      });
    }
    this.updatePlayerUI();
  }

  private showHealOnPlayer(amount: number) {
    this.playerAvatarView?.playHeal();
    const bounds = this.playerAvatarView?.getWorldBounds();
    if (bounds) {
      spawnCombatText(this.scene, {
        kind: "heal",
        value: amount,
        x: bounds.centerX,
        y: bounds.top + 12,
      });
    }
    this.updatePlayerUI();
  }

  private showDefendOnPlayer(armor: number) {
    this.playerAvatarView?.playDefend();
    const bounds = this.playerAvatarView?.getWorldBounds();
    if (bounds) {
      spawnCombatText(this.scene, {
        kind: "shield",
        value: armor,
        x: bounds.centerX,
        y: bounds.top + 12,
        prefix: "🛡️",
      });
    }
    this.updatePlayerUI();
  }

  private showHealOnEnemy(targetIndex: number, amount: number) {
    const view = this.enemyViews[targetIndex];
    if (view) {
      view.avatar.playHeal();
      const bounds = view.avatar.getWorldBounds();
      spawnCombatText(this.scene, {
        kind: "heal",
        value: amount,
        x: bounds.centerX,
        y: bounds.top + 12,
      });
    }
    this.updateEnemyUI();
  }

  private async playCombatEvents(events: CombatEvent[]) {
    for (const event of events) {
      switch (event.kind) {
        case "playerAttack": {
          const view = this.enemyViews[event.targetIndex];
          if (view) {
            await this.telegraphPlayerAttack(view);
            this.drawAttackLine(this.getPlayerCenter(), this.getEnemyCenter(view));
            this.showDamageOnEnemy(event.targetIndex, event.amount);
          }
          break;
        }
        case "enemyAttack": {
          await this.telegraphEnemyAttack(event.attackerIndex);
          const view = this.enemyViews[event.attackerIndex];
          if (view && view.instance.isAlive()) {
            this.drawAttackLine(this.getEnemyCenter(view), this.getPlayerCenter());
          }
          this.showDamageOnPlayer(event.amount);
          break;
        }
        case "playerHeal":
          await this.telegraphPlayerAttack();
          this.showHealOnPlayer(event.amount);
          break;
        case "playerDefend":
          await this.telegraphPlayerAttack();
          this.showDefendOnPlayer(event.armor);
          break;
        case "playerSelfDamage":
          await this.telegraphPlayerAttack();
          this.showDamageOnPlayer(event.amount);
          break;
        case "enemyHeal": {
          this.showHealOnEnemy(event.attackerIndex, event.amount);
          break;
        }
        default:
          break;
      }
    }
  }

  private async playEnemyAttackStep(
    event: Extract<CombatEvent, { kind: "enemyAttack" }>,
    snapshot: CombatSnapshot
  ) {
    await this.telegraphEnemyAttack(event.attackerIndex);
    const view = this.enemyViews[event.attackerIndex];
    if (view && view.instance.isAlive()) {
      this.drawAttackLine(this.getEnemyCenter(view), this.getPlayerCenter());
    }
    this.applySnapshot(snapshot);
    this.showDamageOnPlayer(event.amount);
  }

  private routeAfterResolution(snapshot: CombatSnapshot, prioritizeVictory: boolean) {
    this.updatePlayerUI();
    this.updateEnemyUI();
    this.ensureValidSelection();

    if (snapshot.defeat) {
      this.handleDefeat();
      return true;
    }

    if (prioritizeVictory && snapshot.victory) {
      this.handleVictory();
      return true;
    }

    if (!prioritizeVictory && snapshot.victory) {
      this.handleVictory();
      return true;
    }

    return false;
  }

  private handleVictory() {
    const wasBossEncounter = this.isBossEncounter();

    // Record lifetime run stats (used for run-complete summary).
    this.run.totalEncountersCleared += 1;
    this.run.totalEnemiesDefeated += this.enemies.length;

    if (this.overworldState) {
      this.overworldState.run = this.run;
      applyEncounterVictory(this.overworldState, {
        wasBossEncounter,
        zoneId: this.overworldState.activeZoneId,
      });
    }

    // Boss encounter victory - route to reward/victory screen
    if (wasBossEncounter) {
      const isMaxFloor = isAtMaxFloor(this.run);
      const screenKey: "REWARD" | "RUN_VICTORY" = isMaxFloor ? "RUN_VICTORY" : "REWARD";

      this.scene.overlay.showBanner({
        type: "victory",
        subtitle: `Boss Defeated! Floor ${this.run.floor} cleared`,
      });
      this.delayedCall(OUTCOME_TRANSITION_DELAY, () => {
        this.scene.overlay.transition(
          () =>
            this.scene.screenManager.setScreen(screenKey, {
              run: this.run,
              overworldState: this.overworldState,
            }),
          { keepBanner: true }
        );
      });
      return;
    }

    // Regular encounter victory (always grant reward, even on floor clear)
    const subtitle =
      this.overworldState?.flow === "floorVictory" ? `Floor ${this.run.floor} cleared` : undefined;

    this.scene.overlay.showBanner({
      type: "victory",
      subtitle,
    });
    this.delayedCall(OUTCOME_TRANSITION_DELAY, () => {
      this.scene.overlay.transition(
        () =>
          this.scene.screenManager.setScreen("VICTORY", {
            run: this.run,
            overworldState: this.overworldState,
          }),
        { keepBanner: true }
      );
    });
  }

  private handleDefeat() {
    markRunDefeat(this.run);
    this.scene.overlay.showBanner({
      type: "defeat",
      subtitle: `Fallen on floor ${this.run.floor}`,
    });
    this.delayedCall(OUTCOME_TRANSITION_DELAY, () => {
      this.scene.overlay.transition(
        () =>
          this.scene.screenManager.setScreen("DEFEAT", {
            run: this.run,
            overworldState: this.overworldState,
          }),
        { keepBanner: true }
      );
    });
  }

  private getFirstAliveIndex(): number {
    return this.enemies.findIndex((enemy) => enemy.isAlive());
  }

  private getNextAliveIndex(startIndex: number): number {
    if (this.enemies.length === 0) {
      return -1;
    }

    for (let offset = 1; offset <= this.enemies.length; offset += 1) {
      const index = (startIndex + offset) % this.enemies.length;
      if (this.enemies[index].isAlive()) {
        return index;
      }
    }

    return -1;
  }

  private ensureValidSelection() {
    const firstAliveIndex = this.getFirstAliveIndex();
    if (firstAliveIndex === -1) {
      this.selectedEnemyIndex = -1;
      this.updateSelectionVisuals();
      return;
    }

    if (
      this.selectedEnemyIndex === -1 ||
      !this.enemies[this.selectedEnemyIndex] ||
      !this.enemies[this.selectedEnemyIndex].isAlive()
    ) {
      this.selectedEnemyIndex = this.getNextAliveIndex(
        this.selectedEnemyIndex === -1 ? this.enemies.length - 1 : this.selectedEnemyIndex
      );
    }

    this.updateSelectionVisuals();
  }

  private updateSelectionVisuals() {
    this.enemyViews.forEach((view, index) => {
      const isSelected = index === this.selectedEnemyIndex && view.instance.isAlive();
      view.avatar.setSelected(isSelected);
    });
  }

  private wait(duration: number): Promise<void> {
    return new Promise((resolve) => {
      this.delayedCall(duration, () => resolve());
    });
  }

  private getPlayerCenter(): Phaser.Math.Vector2 {
    const center = this.playerAvatarView?.getWorldCenter();
    return new Phaser.Math.Vector2(center?.x ?? 0, center?.y ?? 0);
  }

  private getEnemyCenter(view: EnemyView): Phaser.Math.Vector2 {
    const center = view.avatar.getWorldCenter();
    return new Phaser.Math.Vector2(center.x, center.y);
  }

  private drawAttackLine(from: Phaser.Math.Vector2, to: Phaser.Math.Vector2) {
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(3, COLORS.buttonStroke, 1);
    graphics.beginPath();
    graphics.moveTo(from.x, from.y);
    graphics.lineTo(to.x, to.y);
    graphics.strokePath();
    graphics.setDepth(ATTACK_LINE_DEPTH);

    this.addToContainer(graphics);
    this.delayedCall(ATTACK_LINE_DURATION, () => graphics.destroy());
  }

  private startPlayerTurn() {
    this.isResolving = false;
    this.isPlayerTurn = true;
    this.updateCommandMenuState(true);
  }

  private updateCommandMenuState(shouldPulse = false) {
    const enabled = this.isPlayerTurn && !this.isResolving;
    if (this.commandMenuContainer) {
      this.commandMenuContainer.setAlpha(enabled ? 1 : 0.65);
      if (enabled && shouldPulse && !this.isResolving) {
        this.pulseCommandMenu();
      }
    }

    this.commandButtons.forEach((button) => {
      button.setEnabled(enabled);
    });
  }

  private pulseCommandMenu() {
    if (!this.commandMenuContainer) {
      return;
    }

    this.commandPulseTween?.stop();
    this.commandMenuContainer.setScale(1);
    this.commandPulseTween = this.addTween({
      targets: this.commandMenuContainer,
      scale: 1.05,
      duration: 160,
      yoyo: true,
      repeat: 1,
      ease: "Sine.easeInOut",
    });
  }

  private async telegraphPlayerAttack(targetView?: EnemyView) {
    if (this.playerRect) {
      this.scene.tweens.killTweensOf(this.playerRect);
      this.addTween({
        targets: this.playerRect,
        alpha: 0.85,
        duration: ACTION_WINDUP,
        yoyo: true,
        ease: "Sine.easeInOut",
      });
    }

    if (targetView) {
      this.scene.tweens.killTweensOf(targetView.container);
      targetView.container.setScale(1);
      this.addTween({
        targets: targetView.container,
        scale: HIGHLIGHT_SCALE,
        duration: ACTION_WINDUP,
        yoyo: true,
        ease: "Sine.easeInOut",
      });
    }

    await this.wait(ACTION_WINDUP);
  }

  private async telegraphEnemyAttack(attackerIndex: number) {
    const view = this.enemyViews[attackerIndex];
    if (view) {
      this.scene.tweens.killTweensOf(view.container);
      view.container.setScale(1);
      this.addTween({
        targets: view.container,
        scale: HIGHLIGHT_SCALE,
        duration: ACTION_WINDUP,
        yoyo: true,
        ease: "Sine.easeInOut",
      });
    }

    if (this.playerRect) {
      this.scene.tweens.killTweensOf(this.playerRect);
      this.addTween({
        targets: this.playerRect,
        alpha: 0.82,
        duration: ACTION_WINDUP,
        yoyo: true,
        ease: "Sine.easeInOut",
      });
    }

    await this.wait(ACTION_WINDUP);
  }

  private registerAutomationTags() {
    // Player stats (HP/ATK/Armor) are now exposed via the persistent HUD.

    this.trackCleanup(
      registerAutomationTarget(this.scene, {
        type: "label",
        name: "turn-state",
        getBounds: () => {
          const view = this.scene.cameras.main.worldView;
          return new Phaser.Geom.Rectangle(view.centerX - 8, view.bottom - 40, 16, 16);
        },
        getText: () => (this.isPlayerTurn ? "player" : "enemy"),
      })
    );
  }
}
